import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import GroupDialog from './GroupDialog';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';
import { createGroup, updateGroup, deleteGroup } from '../../services/group.service';

export default function GroupsManager({ groups, users, onChanged }) {

    const { user } = useAuth();
    const { toast } = useToast();

    const handleCreate = async ({ name, userIds }) => {
        const response = await createGroup(user.token, user.organisationId, { name, userIds });
        if (response && response.status === 201) {
            toast({ title: 'Success', description: 'Group created successfully', variant: 'success' });
            onChanged && onChanged();
            return true;
        }
        const message = response?.data?.error?.message || 'There was an error creating the group';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return false;
    };

    const handleUpdate = async (groupId, { name, userIds }) => {
        const response = await updateGroup(user.token, user.organisationId, groupId, { name, userIds });
        if (response && response.status === 200) {
            toast({ title: 'Success', description: 'Group updated successfully', variant: 'success' });
            onChanged && onChanged();
            return true;
        }
        const message = response?.data?.error?.message || 'There was an error updating the group';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return false;
    };

    const handleDelete = async (groupId) => {
        const response = await deleteGroup(user.token, user.organisationId, groupId);
        if (response && response.status === 200) {
            toast({ title: 'Success', description: 'Group deleted successfully', variant: 'success' });
            onChanged && onChanged();
        } else {
            const message = response?.data?.error?.message || 'There was an error deleting the group';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-yavrio-blue" />
                        <h3 className="my-0">Approval Groups</h3>
                    </div>
                    <GroupDialog users={users} onSave={handleCreate}>
                        <Button className="yvr-button gap-2">
                            <Plus className="w-4 h-4" /> New Group
                        </Button>
                    </GroupDialog>
                </div>
            </CardHeader>
            <CardContent>
                {groups.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        No groups yet. Create a group to reference multiple users as a single approver in your
                        workflows.
                    </p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-white">
                                <TableHead>Name</TableHead>
                                <TableHead>Members</TableHead>
                                <TableHead></TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {groups.map((group) => (
                                <TableRow key={group.id} className="hover:bg-white">
                                    <TableCell className="font-medium">{group.name}</TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {group.members?.length === 0 && (
                                                <span className="text-sm text-gray-400">No members</span>
                                            )}
                                            {group.members?.map((m) => (
                                                <Badge key={m.userId} variant="secondary">{m.name}</Badge>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <GroupDialog
                                            group={group}
                                            users={users}
                                            onSave={(data) => handleUpdate(group.id, data)}
                                        >
                                            <Button variant="ghost" className="p-2 text-yavrio-blue">
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                        </GroupDialog>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <ConfirmDialog
                                            title="Delete Group"
                                            description={
                                                <>
                                                    {`Are you sure you want to delete `}
                                                    <b>{group.name}</b>
                                                    {`? Workflows referencing this group may fail validation.`}
                                                </>
                                            }
                                            cancel="Cancel"
                                            action="Delete"
                                            variant="destructive"
                                            handleAction={() => handleDelete(group.id)}
                                        >
                                            <Button variant="ghost" className="p-2 text-red-400">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </ConfirmDialog>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}
